/**
 * Badge for the derived doctor's-certificate state of a reported absence (#1767).
 * The backend computes the state on read; this only renders it.
 */

import { useLingui } from '@lingui/react';
import { AlertCircle, CheckCircle2, type LucideIcon } from 'lucide-react';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { CertificateStatus, type CertificateStatusValue } from '@/types/employeeAbsences';

interface CertificateStatusBadgeProps {
  status: CertificateStatusValue | (string & {});
  className?: string;
}

/** Translated label of a certificate status; an unknown wire value is returned unchanged. */
export function getCertificateStatusLabel(
  status: CertificateStatusValue | (string & {}),
  i18n: { _: (descriptor: { id: string; message: string }) => string },
): string {
  switch (status) {
    case CertificateStatus.NOT_DUE:
      return i18n._({ id: 'absences.certificateStatus.notDue', message: 'Not due yet' });
    case CertificateStatus.DUE:
      return i18n._({ id: 'absences.certificateStatus.due', message: 'Missing' });
    case CertificateStatus.PROVIDED:
      return i18n._({ id: 'absences.certificateStatus.provided', message: 'Provided' });
    case CertificateStatus.EAU_RETRIEVED:
      return i18n._({ id: 'absences.certificateStatus.eauRetrieved', message: 'eAU retrieved' });
    case CertificateStatus.NOT_REQUIRED:
      return i18n._({ id: 'absences.certificateStatus.notRequired', message: 'Not required' });
    default:
      return status;
  }
}

function getStatusStyle(status: string): { tone: StatusTone; icon?: LucideIcon } {
  switch (status) {
    case CertificateStatus.DUE:
      return { tone: 'danger', icon: AlertCircle };
    case CertificateStatus.PROVIDED:
    case CertificateStatus.EAU_RETRIEVED:
      return { tone: 'success', icon: CheckCircle2 };
    default:
      return { tone: 'neutral' };
  }
}

export function CertificateStatusBadge({ status, className }: CertificateStatusBadgeProps) {
  const { i18n } = useLingui();
  const style = getStatusStyle(status);

  return (
    <StatusBadge
      tone={style.tone}
      icon={style.icon}
      label={getCertificateStatusLabel(status, i18n)}
      className={className}
    />
  );
}
